import { html } from '../../mixins/katapult-element.js';
import { KatapultEditorTemplate } from './katapult-editor-template.js';
import { CamelCase } from '../../modules/CamelCase.js';

import '@polymer/paper-radio-group/paper-radio-group.js';
import '@polymer/paper-radio-button/paper-radio-button.js';
import '../../elements/katapult-drop-down/katapult-drop-down.js';

customElements.define(
  'katapult-proposed-logic-editor',
  class extends KatapultEditorTemplate {
    static get template() {
      return html`
        ${super.style}

        <!-- Populate starting data from database -->
        <katapult-firebase-worker
          path="[[modelSpaceDetails.path]]/models/make_ready/proposed_cable_logic/options"
          data="{{proposedCableOptions}}"
        >
        </katapult-firebase-worker>
        <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/routines" data="{{routines}}"> </katapult-firebase-worker>

        <paper-table>
          <!-- Header row for the table -->
          <paper-row expand>
            <paper-cell icon>
              <material-icon icon="height"></material-icon>
            </paper-cell>
            <paper-cell shrink column align-start style="max-width: 400px;">
              <span>Proposed Cable Insertion Logic</span>
              <span style="font-size: 10pt;" italic>
                Controls where proposed cables are inserted into photos when using the Insert Proposed shortcut
              </span>
            </paper-cell>
          </paper-row>
          <!-- Row for insertion logic options -->
          <paper-row expand>
            <!-- Spacer -->
            <paper-cell icon></paper-cell>
            <paper-cell>
              <paper-table rounded outline grow dividers overflow>
                <paper-row expand>
                  <paper-cell>
                    <div style="display: flex; align-items: center; padding: 8px 0;">
                      <paper-radio-group selected="{{selectedInsertionLogic}}">
                        <template is="dom-repeat" items="[[insertionLogicOptions]]">
                          <paper-radio-button name="[[item.value]]">
                            [[item.label]]
                            <br />
                            <span style="font-size: 10pt;" italic>[[item.description]]</span>
                          </paper-radio-button>
                          <br />
                        </template>
                      </paper-radio-group>
                    </div>
                  </paper-cell>
                </paper-row>
              </paper-table>
            </paper-cell>
          </paper-row>

          <!-- Row for choosing what type of photo routine gets used for proposed cables -->
          <paper-row expand>
            <paper-cell icon>
              <material-icon icon="image"></material-icon>
            </paper-cell>
            <paper-cell shrink column align-start style="max-width: 400px;">
              <span>Proposed Photo Routine</span>
              <span style="font-size: 10pt;" italic
                >Controls which Photo Routine is used when you Insert Proposed. If no Photo Routine is selected, a bare wire will be
                used.</span
              >
              <span style="font-size: 10pt; margin-top: 10px;" italic>
                <strong>Note:</strong> You can only chose from photo routines that include a single wire. If you Insert Proposed and there
                is already an existing proposed cable, the new proposed cable will be modelled off of the existing proposed cable and not
                from this photo routine.
              </span>
            </paper-cell>
            <paper-cell style="max-height: 50px; border: 1px solid rgba(0, 0, 0, 0.1); border-radius: 20px; align-items: center;">
              <katapult-drop-down
                items="[[photoInputsArray]]"
                data-path="_itemData.options.proposed_photo_input"
                value="[[proposedCableOptions.proposed_photo_input]]"
                on-selected-changed="updateProperty"
                label="Select Photo Routine"
                label-path="label"
                value-path="value"
                no-label-float
              >
              </katapult-drop-down>
            </paper-cell>
          </paper-row>

          <!-- Options for when proposed cables move -->
          <paper-row expand>
            <paper-cell icon>
              <material-icon icon="tune"></material-icon>
            </paper-cell>
            <paper-cell shrink column align-start style="max-width: 400px;">
              <span>Proposed Cable Options</span>
              <span style="font-size: 10pt;" italic>Controls what happens when proposed cables are manipulated</span>
            </paper-cell>
          </paper-row>
          <!-- Row for insertion logic options -->
          <paper-row expand>
            <!-- Spacer -->
            <paper-cell icon></paper-cell>
            <paper-cell>
              <paper-table rounded grow dividers overflow>
                <!-- Option for moving the proposed cable in the midspan when it's moved on the pole -->
                <paper-row expand>
                  <paper-cell>
                    <paper-checkbox
                      data-path="_itemData.options.adjust_proposed_cable_in_midspan"
                      checked="[[proposedCableOptions.adjust_proposed_cable_in_midspan]]"
                      on-checked-changed="updateProperty"
                    >
                      Adjust the height of the proposed cable in the midspan if its traced cable on the pole is moved
                    </paper-checkbox>
                  </paper-cell>
                </paper-row>
              </paper-table>
            </paper-cell>
          </paper-row>
        </paper-table>
      `;
    }

    constructor() {
      super();
    }

    ready() {
      super.ready();
    }

    static get observers() {
      return [
        'setupInsertionLogic(_itemData.insertion_logic)',
        'selectedInsertionLogicChanged(selectedInsertionLogic)',
        'setupPhotoInputArray(routines)'
      ];
    }

    /**
     * Builds a list of insertion logic options from the model and sets insertionLogicOptions. Also sets
     * the selected logic option if one is already selected in the model. Basic is selected if none already are.
     * The basic logic option is hard coded here and applied if it's missing from the model.
     */
    setupInsertionLogic() {
      let basicLogic = {
        value: 'basic',
        label: 'Basic',
        description: [
          `If there are comm attachments*, inserts 12" above. If there are power attachments**, inserts 40" below on a pole and 30" below in a midspan. If no power or comm attachments are present, inserts at 25'. May additionally adjust position if clearances require it.`,
          `*comm attachment as defined by a wire marker with Cable Type set to a value in the Communications picklist.`,
          `**power attachment as defined by a wire marker with Cable Type set to a value in the Power picklist or an equipment marker with Company set to a value in the Power picklist.`
        ]
      };

      // Add the basic option if it's missing
      if (!this._itemData?.insertion_logic?.basic) {
        this.deepSet(`_itemData.insertion_logic.basic`, basicLogic);
      }

      // Get options from the model
      let options = this._itemData?.insertion_logic || {};

      // Set the selected logic option, falling back to basic if none are already selected
      const activeKey = this.getActiveInsertionLogic();
      if (activeKey) {
        this.selectedInsertionLogic = activeKey;
      } else {
        this.selectedInsertionLogic = 'basic';
      }

      // Convert options to an array
      options = Object.values(options);

      // Sort basic to the top
      options.sort((a) => (a.value === 'basic' ? -1 : 1));

      this.insertionLogicOptions = options;
    }

    /**
     * Returns the logic record that is set to active
     * @returns {Object} The insertion logic object that is selected
     */
    getActiveInsertionLogic() {
      const existingData = this._itemData?.insertion_logic || {};
      return Object.keys(existingData).find((key) => existingData[key].active);
    }

    /**
     * Observer fired when the selected logic changes. Deselects any currently selected logic and selects the new one.
     */
    selectedInsertionLogicChanged() {
      const keyToDeactivate = this.getActiveInsertionLogic();

      // If the current and new keys are the same, do nothing
      if (keyToDeactivate == this.selectedInsertionLogic) return;

      let propertiesUpdate = {};
      if (keyToDeactivate) {
        propertiesUpdate[`_itemData.insertion_logic.${keyToDeactivate}.active`] = null;
      }

      if (this.selectedInsertionLogic) {
        propertiesUpdate[`_itemData.insertion_logic.${this.selectedInsertionLogic}.active`] = true;
      }

      this.setProperties(propertiesUpdate);
    }

    /**
     * Recursively counts the number of wires in a given array of items
     * @param {Array} items - The array of items to count wires in
     * @param {Object} currentCount - The current count of wires as an object so it gets saved between recursive calls
     * @returns {void}
     */
    countWires(items, currentCount) {
      for (let item of items) {
        // add to the count if the item is a wire
        let type = item.type;
        if (type == 'wire') currentCount.count++;
        // run the countWires function on children of the item
        let children = item?.attributes?._children;
        if (children && children.length != 0) this.countWires(children, currentCount);
      }
    }

    /**
     * Takes a given object of routines and sets up the photoInputsArray with the routines that have only 1 wire and one item
     * @param {Object} routines - Starting list of routines
     * @returns {void}
     */
    setupPhotoInputArray(routines) {
      // Wait for routines and inputModels to be loaded
      if (!routines) return;

      var list = [];

      // add the wire input model
      list.push({ value: 'wire', label: 'Wire' });

      let routineEntries = Object.entries(routines ?? {});
      // filter the routines to only include items that have 1 wire and that only have 1 item
      let wireRoutineEntries = routineEntries.filter((routineEntry) => {
        let items = routineEntry[1].items;
        if (!items || items.length != 1) return false;
        // count the wires in the items
        let count = { count: 0 };
        this.countWires(items, count);
        if (count.count == 1) return true;
      });

      // all the routines that have only 1 wire
      for (const [key, routine] of wireRoutineEntries) {
        //check to see if this item has been added in some fashion to the list array
        if (list.find((x) => x?.value.toLowerCase() == key.toLowerCase())) continue;
        list.push({ value: key, label: routine.name ?? CamelCase(key) });
      }
      //sort alphabetically
      list.sort((a, b) => a.label.localeCompare(b.label));
      // set the photo inputs array
      this.photoInputsArray = list;
    }
  }
);
